import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import AdminLayout from "@/components/layout-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useApartments } from "@/hooks/use-resources";
import {
  Receipt, Plus, Trash2, Edit, DollarSign, TrendingDown,
  Search, Building2, Package, RefreshCw, ShoppingCart,
  ToggleLeft, ToggleRight,
} from "lucide-react";
import type { UnitCost, SupplyCatalogItem } from "@shared/schema";

const costCategories = [
  { value: "supplies", label: "Supplies" },
  { value: "maintenance", label: "Maintenance" },
  { value: "utilities", label: "Utilities" },
  { value: "utility_water", label: "Water" },
  { value: "utility_electric", label: "Electricity" },
  { value: "utility_gas", label: "Gas" },
  { value: "repairs", label: "Repairs" },
  { value: "cleaning_supplies", label: "Cleaning Supplies" },
  { value: "furniture", label: "Furniture" },
  { value: "appliances", label: "Appliances" },
  { value: "insurance", label: "Insurance" },
  { value: "taxes", label: "Taxes & Fees" },
  { value: "marketing", label: "Marketing" },
  { value: "software", label: "Software & Tools" },
  { value: "other", label: "Other" },
];

const supplyCategories = [
  { value: "cleaning", label: "Cleaning" },
  { value: "bathroom", label: "Bathroom" },
  { value: "kitchen", label: "Kitchen" },
  { value: "bedroom", label: "Bedroom" },
  { value: "laundry", label: "Laundry" },
  { value: "amenities", label: "Guest Amenities" },
  { value: "other", label: "Other" },
];

const unitMeasures = [
  { value: "unit", label: "Unit" },
  { value: "roll", label: "Roll" },
  { value: "pack", label: "Pack" },
  { value: "bottle", label: "Bottle" },
  { value: "bag", label: "Bag" },
  { value: "box", label: "Box" },
  { value: "sheet", label: "Sheet" },
  { value: "pair", label: "Pair" },
  { value: "set", label: "Set" },
  { value: "gallon", label: "Gallon" },
  { value: "oz", label: "Oz" },
  { value: "lb", label: "Lb" },
];

const recurringOptions = [
  { value: "per_cleaning", label: "Per Cleaning (auto)" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

interface CostForm {
  apartmentId: string;
  category: string;
  description: string;
  amount: string;
  date: string;
  recurring: boolean;
  recurringFrequency: string;
  vendor: string;
  chargedToPm: boolean;
  isOrgExpense: boolean;
  paidToCleaner: boolean;
  marginPct: string;
}

const emptyForm: CostForm = {
  apartmentId: "", category: "", description: "", amount: "",
  date: new Date().toISOString().split("T")[0], recurring: false,
  recurringFrequency: "", vendor: "",
  chargedToPm: false, isOrgExpense: false, paidToCleaner: false, marginPct: "",
};

interface SupplyForm {
  name: string;
  category: string;
  unitOfMeasure: string;
  costPerUnit: string;
  qtyPerJobStudio: string;
  qtyPerJob1br: string;
  qtyPerJob2br: string;
  qtyPerJob3br: string;
  qtyPerJob1bath: string;
  qtyPerJob2bath: string;
  qtyPerJob3bath: string;
  active: boolean;
  typicalOwner: string;
}

const emptySupplyForm: SupplyForm = {
  name: "", category: "cleaning", unitOfMeasure: "unit",
  costPerUnit: "", qtyPerJobStudio: "1", qtyPerJob1br: "1",
  qtyPerJob2br: "2", qtyPerJob3br: "3",
  qtyPerJob1bath: "0", qtyPerJob2bath: "0", qtyPerJob3bath: "0",
  active: true, typicalOwner: "org",
};

export default function AdminUnitCosts() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("supplies");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<UnitCost | null>(null);
  const [form, setForm] = useState<CostForm>(emptyForm);
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterApartment, setFilterApartment] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [supplyDialogOpen, setSupplyDialogOpen] = useState(false);
  const [editingSupply, setEditingSupply] = useState<SupplyCatalogItem | null>(null);
  const [deleteConfirmCost, setDeleteConfirmCost] = useState<UnitCost | null>(null);
  const [deleteConfirmSupply, setDeleteConfirmSupply] = useState<SupplyCatalogItem | null>(null);
  const [supplyForm, setSupplyForm] = useState<SupplyForm>(emptySupplyForm);

  const { data: costs = [], isLoading } = useQuery<UnitCost[]>({
    queryKey: ["/api/admin/unit-costs"],
  });

  const { data: apartments = [] } = useApartments();

  const { data: summary } = useQuery<{ total: number; byCategory: Record<string, number>; byApartment: Record<number, number>; count: number }>({
    queryKey: ["/api/admin/unit-costs/summary"],
  });

  const { data: catalog = [] } = useQuery<SupplyCatalogItem[]>({
    queryKey: ["/api/admin/supply-catalog"],
  });

  const { data: costPerJob } = useQuery<{ costByBedrooms: Record<number, number>; costByBathrooms: Record<number, number>; breakdown: { name: string; costPerUnit: number; quantities: Record<number, number>; costs: Record<number, number>; bathroomQuantities: Record<number, number>; bathroomCosts: Record<number, number> }[]; itemCount: number }>({
    queryKey: ["/api/admin/supply-catalog/cost-per-job"],
  });

  const createCost = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/unit-costs", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-costs"] });
      setCreateOpen(false);
      setForm(emptyForm);
      toast({ title: "Cost record created" });
    },
    onError: (e: any) => toast({ title: "Failed to create cost", description: e.message, variant: "destructive" }),
  });

  const updateCost = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest("PUT", `/api/admin/unit-costs/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-costs"] });
      setEditingCost(null);
      setForm(emptyForm);
      toast({ title: "Cost updated" });
    },
    onError: (e: any) => toast({ title: "Failed to update cost", description: e.message, variant: "destructive" }),
  });

  const deleteCost = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/unit-costs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/unit-costs"] });
      toast({ title: "Cost deleted" });
    },
  });

  const createSupply = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/supply-catalog", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/supply-catalog"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/supply-catalog/cost-per-job"] });
      setSupplyDialogOpen(false);
      setSupplyForm(emptySupplyForm);
      toast({ title: "Supply item added" });
    },
    onError: (e: any) => toast({ title: "Failed to add supply", description: e.message, variant: "destructive" }),
  });

  const updateSupply = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest("PUT", `/api/admin/supply-catalog/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/supply-catalog"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/supply-catalog/cost-per-job"] });
      setEditingSupply(null);
      setSupplyForm(emptySupplyForm);
      toast({ title: "Supply item updated" });
    },
    onError: (e: any) => toast({ title: "Failed to update supply", description: e.message, variant: "destructive" }),
  });

  const deleteSupply = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/supply-catalog/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/supply-catalog"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/supply-catalog/cost-per-job"] });
      toast({ title: "Supply item deleted" });
    },
  });

  const handleCostSubmit = () => {
    const data = {
      apartmentId: form.apartmentId ? parseInt(form.apartmentId) : null,
      category: form.category,
      description: form.description || null,
      amount: form.amount,
      date: form.date,
      recurring: form.recurring,
      recurringFrequency: form.recurring ? form.recurringFrequency || null : null,
      vendor: form.vendor || null,
      chargedToPm: form.chargedToPm,
      isOrgExpense: form.isOrgExpense,
      paidToCleaner: form.paidToCleaner,
      marginPct: form.marginPct || null,
    };
    if (editingCost) {
      updateCost.mutate({ id: editingCost.id, data });
    } else {
      createCost.mutate(data);
    }
  };

  const handleSupplySubmit = () => {
    const data = {
      name: supplyForm.name,
      category: supplyForm.category,
      unitOfMeasure: supplyForm.unitOfMeasure,
      costPerUnit: supplyForm.costPerUnit,
      qtyPerJobStudio: supplyForm.qtyPerJobStudio || "0",
      qtyPerJob1br: supplyForm.qtyPerJob1br || "0",
      qtyPerJob2br: supplyForm.qtyPerJob2br || "0",
      qtyPerJob3br: supplyForm.qtyPerJob3br || "0",
      qtyPerJob1bath: supplyForm.qtyPerJob1bath || "0",
      qtyPerJob2bath: supplyForm.qtyPerJob2bath || "0",
      qtyPerJob3bath: supplyForm.qtyPerJob3bath || "0",
      active: supplyForm.active,
      typicalOwner: supplyForm.typicalOwner || "org",
    };
    if (editingSupply) {
      updateSupply.mutate({ id: editingSupply.id, data });
    } else {
      createSupply.mutate(data);
    }
  };

  const openEditCost = (cost: UnitCost) => {
    setEditingCost(cost);
    setForm({
      apartmentId: cost.apartmentId?.toString() || "",
      category: cost.category,
      description: cost.description || "",
      amount: cost.amount,
      date: cost.date ? new Date(cost.date).toISOString().split("T")[0] : "",
      chargedToPm: cost.chargedToPm ?? false,
      isOrgExpense: cost.isOrgExpense ?? false,
      paidToCleaner: cost.paidToCleaner ?? false,
      marginPct: cost.marginPct || "",
      recurring: cost.recurring || false,
      recurringFrequency: cost.recurringFrequency || "",
      vendor: cost.vendor || "",
    });
  };

  const openEditSupply = (item: SupplyCatalogItem) => {
    setEditingSupply(item);
    setSupplyForm({
      name: item.name,
      category: item.category,
      unitOfMeasure: item.unitOfMeasure,
      costPerUnit: item.costPerUnit,
      qtyPerJobStudio: item.qtyPerJobStudio,
      qtyPerJob1br: item.qtyPerJob1br,
      qtyPerJob2br: item.qtyPerJob2br,
      qtyPerJob3br: item.qtyPerJob3br,
      qtyPerJob1bath: (item as any).qtyPerJob1bath ?? "0",
      qtyPerJob2bath: (item as any).qtyPerJob2bath ?? "0",
      qtyPerJob3bath: (item as any).qtyPerJob3bath ?? "0",
      active: item.active,
      typicalOwner: item.typicalOwner || "org",
    });
  };

  const filteredCosts = costs.filter(c => {
    if (filterCategory !== "all" && c.category !== filterCategory) return false;
    if (filterApartment !== "all" && c.apartmentId?.toString() !== filterApartment) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (c.description || "").toLowerCase().includes(q) ||
        c.category.toLowerCase().includes(q) ||
        (c.vendor || "").toLowerCase().includes(q);
    }
    return true;
  });

  const topCategories = summary?.byCategory
    ? Object.entries(summary.byCategory).sort((a, b) => b[1] - a[1]).slice(0, 5)
    : [];

  const activeCatalogItems = catalog.filter(i => i.active);
  const totalCostPerJobByType = costPerJob?.costByBedrooms || { 0: 0, 1: 0, 2: 0, 3: 0 };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="unit-costs-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-display font-bold flex items-center gap-2" data-testid="unit-costs-title">
              <Receipt className="h-6 w-6 text-primary" />
              Unit Costs
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Track supplies per cleaning job and general expenses per apartment</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="supplies" className="gap-1.5" data-testid="tab-supplies">
              <ShoppingCart className="h-4 w-4" />
              Supply Catalog
            </TabsTrigger>
            <TabsTrigger value="expenses" className="gap-1.5" data-testid="tab-expenses">
              <Receipt className="h-4 w-4" />
              General Expenses
            </TabsTrigger>
          </TabsList>

          <TabsContent value="supplies" className="space-y-6 mt-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card><CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Items</p>
                    <p className="text-xl font-bold" data-testid="stat-catalog-items">{catalog.length}</p>
                  </div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Studio/job</p>
                    <p className="text-xl font-bold" data-testid="stat-cost-studio">${totalCostPerJobByType[0]?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">1BR/job</p>
                    <p className="text-xl font-bold" data-testid="stat-cost-1br">${totalCostPerJobByType[1]?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">2BR/job</p>
                    <p className="text-xl font-bold" data-testid="stat-cost-2br">${totalCostPerJobByType[2]?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </CardContent></Card>
            </div>

            {costPerJob && costPerJob.breakdown.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Cost Breakdown per Cleaning Job — by Bedrooms</span>
                    <Badge variant="outline" className="text-xs">
                      3BR: ${totalCostPerJobByType[3]?.toFixed(2) || '0.00'}/job
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm" data-testid="table-cost-breakdown">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-2 font-medium text-muted-foreground">Supply</th>
                          <th className="pb-2 font-medium text-muted-foreground text-right">Cost/Unit</th>
                          <th className="pb-2 font-medium text-muted-foreground text-center">Studio</th>
                          <th className="pb-2 font-medium text-muted-foreground text-center">1BR</th>
                          <th className="pb-2 font-medium text-muted-foreground text-center">2BR</th>
                          <th className="pb-2 font-medium text-muted-foreground text-center">3+ BR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costPerJob.breakdown.map((item, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-2 font-medium">{item.name}</td>
                            <td className="py-2 text-right">${item.costPerUnit.toFixed(2)}</td>
                            <td className="py-2 text-center">
                              <span className="text-muted-foreground">{item.quantities[0]}x</span>
                              <span className="ml-1 font-medium">${item.costs[0].toFixed(2)}</span>
                            </td>
                            <td className="py-2 text-center">
                              <span className="text-muted-foreground">{item.quantities[1]}x</span>
                              <span className="ml-1 font-medium">${item.costs[1].toFixed(2)}</span>
                            </td>
                            <td className="py-2 text-center">
                              <span className="text-muted-foreground">{item.quantities[2]}x</span>
                              <span className="ml-1 font-medium">${item.costs[2].toFixed(2)}</span>
                            </td>
                            <td className="py-2 text-center">
                              <span className="text-muted-foreground">{item.quantities[3]}x</span>
                              <span className="ml-1 font-medium">${item.costs[3].toFixed(2)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 font-bold">
                          <td className="pt-3" colSpan={2}>Total per job</td>
                          <td className="pt-3 text-center text-primary">${totalCostPerJobByType[0]?.toFixed(2)}</td>
                          <td className="pt-3 text-center text-primary">${totalCostPerJobByType[1]?.toFixed(2)}</td>
                          <td className="pt-3 text-center text-primary">${totalCostPerJobByType[2]?.toFixed(2)}</td>
                          <td className="pt-3 text-center text-primary">${totalCostPerJobByType[3]?.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {costPerJob.breakdown.some(item => Object.values(item.bathroomQuantities ?? {}).some(q => q > 0)) && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Additional cost by bathrooms (added to bedroom cost)</p>
                      <table className="w-full text-xs sm:text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 font-medium text-muted-foreground">Supply</th>
                            <th className="pb-2 font-medium text-muted-foreground text-center">1 Bath</th>
                            <th className="pb-2 font-medium text-muted-foreground text-center">2 Bath</th>
                            <th className="pb-2 font-medium text-muted-foreground text-center">3+ Bath</th>
                          </tr>
                        </thead>
                        <tbody>
                          {costPerJob.breakdown.filter(item => Object.values(item.bathroomQuantities ?? {}).some(q => q > 0)).map((item, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="py-2 font-medium">{item.name}</td>
                              {[1, 2, 3].map(bath => (
                                <td key={bath} className="py-2 text-center">
                                  <span className="text-muted-foreground">{(item.bathroomQuantities ?? {})[bath] ?? 0}x</span>
                                  <span className="ml-1 font-medium">${((item.bathroomCosts ?? {})[bath] ?? 0).toFixed(2)}</span>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 font-bold">
                            <td className="pt-3">Total bathroom add-on</td>
                            {[1, 2, 3].map(bath => (
                              <td key={bath} className="pt-3 text-center text-primary">
                                ${costPerJob.costByBathrooms?.[bath]?.toFixed(2) ?? '0.00'}
                              </td>
                            ))}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Supply Items</h2>
              <Button className="bg-primary hover:bg-primary/90" onClick={() => { setSupplyDialogOpen(true); setSupplyForm(emptySupplyForm); setEditingSupply(null); }} data-testid="add-supply-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Supply Item
              </Button>
            </div>

            {catalog.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <ShoppingCart className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-lg">No supply items yet</h3>
                  <p className="text-sm text-muted-foreground mt-1 mb-4 text-center max-w-sm">
                    Add the supplies used in each cleaning job (toilet paper, paper towels, etc.) to calculate real costs per unit.
                  </p>
                  <Button className="bg-primary hover:bg-primary/90" onClick={() => { setSupplyDialogOpen(true); setSupplyForm(emptySupplyForm); }}>
                    <Plus className="h-4 w-4 mr-2" /> Add First Supply
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {catalog.map(item => {
                  const cpu = parseFloat(item.costPerUnit) || 0;
                  const catLabel = supplyCategories.find(c => c.value === item.category)?.label || item.category;
                  return (
                    <Card key={item.id} className={`${!item.active ? 'opacity-50' : ''}`} data-testid={`supply-item-${item.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-medium text-sm">{item.name}</span>
                              <Badge variant="outline" className="text-[10px]">{catLabel}</Badge>
                              {!item.active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                              <span>${cpu.toFixed(2)} / {item.unitOfMeasure}</span>
                              <span>Studio: {item.qtyPerJobStudio}x</span>
                              <span>1BR: {item.qtyPerJob1br}x</span>
                              <span>2BR: {item.qtyPerJob2br}x</span>
                              <span>3BR: {item.qtyPerJob3br}x</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => openEditSupply(item)} data-testid={`edit-supply-${item.id}`}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmSupply(item)} data-testid={`delete-supply-${item.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="expenses" className="space-y-6 mt-4">
            <div className="flex items-center justify-between">
              <div />
              <Button className="bg-primary hover:bg-primary/90" onClick={() => { setCreateOpen(true); setForm(emptyForm); setEditingCost(null); }} data-testid="new-cost-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Expense
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card><CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900 flex items-center justify-center">
                    <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Expenses</p>
                    <p className="text-xl font-bold" data-testid="stat-total-costs">${(summary?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <Receipt className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Records</p>
                    <p className="text-xl font-bold" data-testid="stat-total-records">{summary?.count || 0}</p>
                  </div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Apartments</p>
                    <p className="text-xl font-bold" data-testid="stat-apartments-tracked">{Object.keys(summary?.byApartment || {}).length}</p>
                  </div>
                </div>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                    <Package className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Categories</p>
                    <p className="text-xl font-bold" data-testid="stat-categories">{Object.keys(summary?.byCategory || {}).length}</p>
                  </div>
                </div>
              </CardContent></Card>
            </div>

            {topCategories.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Top Expense Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {topCategories.map(([cat, amount]) => {
                      const catLabel = costCategories.find(c => c.value === cat)?.label || cat;
                      const pct = summary?.total ? (amount / summary.total * 100) : 0;
                      return (
                        <div key={cat} className="flex items-center gap-3" data-testid={`category-bar-${cat}`}>
                          <span className="text-xs font-medium w-28 truncate">{catLabel}</span>
                          <div className="flex-1 bg-muted rounded-full h-2.5 overflow-hidden">
                            <div className="bg-primary h-full rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-medium text-right w-24">${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input className="pl-8 h-9 text-sm" placeholder="Search by description, category, or vendor..." value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)} data-testid="input-search-costs" />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full sm:w-40 h-9 text-sm" data-testid="filter-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {costCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <SearchableSelect
                value={filterApartment}
                onValueChange={setFilterApartment}
                placeholder="All Apartments"
                searchPlaceholder="Search apartment..."
                options={[
                  { value: "all", label: "All Apartments" },
                  ...apartments.map(a => ({ value: a.id.toString(), label: a.name })),
                ]}
                className="w-full sm:w-40 h-9 text-sm"
                data-testid="filter-apartment"
              />
            </div>

            <div className="space-y-2">
              {isLoading ? (
                <div className="text-center py-12 text-muted-foreground">Loading expenses...</div>
              ) : filteredCosts.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                      <Receipt className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="font-semibold text-lg">No expense records yet</h3>
                    <p className="text-sm text-muted-foreground mt-1 mb-4">Track one-time expenses like paint, repairs, and general costs</p>
                    <Button className="bg-primary hover:bg-primary/90" onClick={() => { setCreateOpen(true); setForm(emptyForm); }}>
                      <Plus className="h-4 w-4 mr-2" /> Add First Expense
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Category</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Description</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Apartment</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground uppercase">Vendor</th>
                          <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">Amount</th>
                          <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredCosts.map(cost => {
                          const apt = apartments.find(a => a.id === cost.apartmentId);
                          const catLabel = costCategories.find(c => c.value === cost.category)?.label || cost.category;
                          return (
                            <tr key={cost.id} className="hover:bg-muted/30" data-testid={`cost-row-${cost.id}`}>
                              <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                                {cost.date ? new Date(cost.date).toLocaleDateString("en-US", { timeZone: "UTC" }) : "-"}
                              </td>
                              <td className="px-4 py-2.5">
                                <Badge variant="outline" className="text-xs">{catLabel}</Badge>
                                {cost.recurring && <Badge variant="secondary" className="text-[10px] ml-1">Recurring</Badge>}
                              </td>
                              <td className="px-4 py-2.5 text-xs max-w-[200px] truncate">{cost.description || "-"}</td>
                              <td className="px-4 py-2.5 text-xs">{apt?.name || (cost.apartmentId ? `#${cost.apartmentId}` : "General")}</td>
                              <td className="px-4 py-2.5 text-xs text-muted-foreground">{cost.vendor || "-"}</td>
                              <td className="px-4 py-2.5 text-right text-xs font-medium text-red-600 dark:text-red-400">
                                ${parseFloat(cost.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => openEditCost(cost)} data-testid={`edit-cost-${cost.id}`}>
                                    <Edit className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmCost(cost)} data-testid={`delete-cost-${cost.id}`}>
                                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={createOpen || !!editingCost} onOpenChange={v => { if (!v) { setCreateOpen(false); setEditingCost(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCost ? "Edit Expense" : "Add Expense"}</DialogTitle>
            <DialogDescription>Record a general expense like paint, repairs, or one-time costs</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Category *</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="input-cost-category"><SelectValue placeholder="Select category..." /></SelectTrigger>
                <SelectContent>
                  {costCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Amount *</label>
              <Input type="number" step="0.01" placeholder="0.00" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="input-cost-amount" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Date *</label>
              <Input type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="input-cost-date" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Apartment</label>
              <SearchableSelect
                value={form.apartmentId || "none"}
                onValueChange={v => setForm(f => ({ ...f, apartmentId: v === "none" ? "" : v }))}
                placeholder="Select apartment..."
                searchPlaceholder="Search apartment..."
                options={[
                  { value: "none", label: "General (no apartment)" },
                  ...apartments.map(a => ({ value: a.id.toString(), label: a.name })),
                ]}
                className="w-full"
                data-testid="input-cost-apartment"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea placeholder="Details about this expense..." value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="input-cost-description" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Vendor</label>
              <Input placeholder="e.g., Home Depot, Amazon..." value={form.vendor}
                onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} data-testid="input-cost-vendor" />
            </div>
            <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Billing Options</p>
              <div>
                <label className="text-sm font-medium mb-1 block">Charge to</label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={!form.chargedToPm && !form.isOrgExpense ? "default" : "outline"}
                    onClick={() => setForm(f => ({ ...f, chargedToPm: false, isOrgExpense: false }))}
                    data-testid="charge-to-internal"
                  >
                    Internal
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={form.chargedToPm ? "default" : "outline"}
                    onClick={() => setForm(f => ({ ...f, chargedToPm: true, isOrgExpense: false }))}
                    data-testid="charge-to-pm"
                  >
                    Property Mgr
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={form.isOrgExpense ? "default" : "outline"}
                    onClick={() => setForm(f => ({ ...f, isOrgExpense: true, chargedToPm: false }))}
                    data-testid="charge-to-org"
                  >
                    Org Expense
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {form.isOrgExpense
                    ? "Absorbed by the org — reduces admin profit, never billed to a PM."
                    : form.chargedToPm
                      ? "Passed to the property manager on their invoice (with margin below)."
                      : "Internal cost — not billed to a PM and not netted against profit."}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Paid to Cleaner</label>
                  <p className="text-xs text-muted-foreground">This expense is paid directly to the cleaner</p>
                </div>
                <Switch checked={form.paidToCleaner} onCheckedChange={v => setForm(f => ({ ...f, paidToCleaner: v }))} data-testid="toggle-paid-to-cleaner" />
              </div>
              {form.chargedToPm && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Margin (%)</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="e.g. 15"
                    value={form.marginPct}
                    onChange={e => setForm(f => ({ ...f, marginPct: e.target.value }))}
                    data-testid="input-margin-pct"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {form.marginPct && form.amount
                      ? `PM will be charged $${(parseFloat(form.amount) * (1 + parseFloat(form.marginPct) / 100)).toFixed(2)}`
                      : "Percentage added on top of the amount charged to the PM"}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Recurring Cost</label>
              <Switch checked={form.recurring} onCheckedChange={v => setForm(f => ({ ...f, recurring: v }))} data-testid="toggle-recurring" />
            </div>
            {form.recurring && (
              <div>
                <label className="text-sm font-medium mb-1 block">Frequency</label>
                <Select value={form.recurringFrequency} onValueChange={v => setForm(f => ({ ...f, recurringFrequency: v }))}>
                  <SelectTrigger data-testid="input-recurring-frequency"><SelectValue placeholder="Select frequency..." /></SelectTrigger>
                  <SelectContent>
                    {recurringOptions.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button className="w-full bg-primary hover:bg-primary/90"
              disabled={!form.category || !form.amount || !form.date || createCost.isPending || updateCost.isPending}
              onClick={handleCostSubmit} data-testid="submit-cost-btn">
              {(createCost.isPending || updateCost.isPending) && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {editingCost ? "Save Changes" : "Add Expense"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={supplyDialogOpen || !!editingSupply} onOpenChange={v => { if (!v) { setSupplyDialogOpen(false); setEditingSupply(null); setSupplyForm(emptySupplyForm); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSupply ? "Edit Supply Item" : "Add Supply Item"}</DialogTitle>
            <DialogDescription>Define a supply used per cleaning job with quantities by apartment type</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Item Name *</label>
              <Input placeholder="e.g., Toilet Paper, Paper Towels..." value={supplyForm.name}
                onChange={e => setSupplyForm(f => ({ ...f, name: e.target.value }))} data-testid="input-supply-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Category</label>
                <Select value={supplyForm.category} onValueChange={v => setSupplyForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="input-supply-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {supplyCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Unit of Measure</label>
                <Select value={supplyForm.unitOfMeasure} onValueChange={v => setSupplyForm(f => ({ ...f, unitOfMeasure: v }))}>
                  <SelectTrigger data-testid="input-supply-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {unitMeasures.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Cost per {supplyForm.unitOfMeasure || 'unit'} ($) *</label>
              <Input type="number" step="0.01" placeholder="0.00" value={supplyForm.costPerUnit}
                onChange={e => setSupplyForm(f => ({ ...f, costPerUnit: e.target.value }))} data-testid="input-supply-cost" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Qty by bedrooms</label>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-0.5 block">Studio</label>
                  <Input type="number" step="0.5" min="0" value={supplyForm.qtyPerJobStudio}
                    onChange={e => setSupplyForm(f => ({ ...f, qtyPerJobStudio: e.target.value }))} data-testid="input-qty-studio" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-0.5 block">1 BR</label>
                  <Input type="number" step="0.5" min="0" value={supplyForm.qtyPerJob1br}
                    onChange={e => setSupplyForm(f => ({ ...f, qtyPerJob1br: e.target.value }))} data-testid="input-qty-1br" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-0.5 block">2 BR</label>
                  <Input type="number" step="0.5" min="0" value={supplyForm.qtyPerJob2br}
                    onChange={e => setSupplyForm(f => ({ ...f, qtyPerJob2br: e.target.value }))} data-testid="input-qty-2br" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-0.5 block">3+ BR</label>
                  <Input type="number" step="0.5" min="0" value={supplyForm.qtyPerJob3br}
                    onChange={e => setSupplyForm(f => ({ ...f, qtyPerJob3br: e.target.value }))} data-testid="input-qty-3br" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Additional qty by bathrooms <span className="text-muted-foreground font-normal">(added on top of bedroom qty)</span></label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-0.5 block">1 Bath</label>
                  <Input type="number" step="0.5" min="0" value={supplyForm.qtyPerJob1bath}
                    onChange={e => setSupplyForm(f => ({ ...f, qtyPerJob1bath: e.target.value }))} data-testid="input-qty-1bath" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-0.5 block">2 Bath</label>
                  <Input type="number" step="0.5" min="0" value={supplyForm.qtyPerJob2bath}
                    onChange={e => setSupplyForm(f => ({ ...f, qtyPerJob2bath: e.target.value }))} data-testid="input-qty-2bath" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-0.5 block">3+ Bath</label>
                  <Input type="number" step="0.5" min="0" value={supplyForm.qtyPerJob3bath}
                    onChange={e => setSupplyForm(f => ({ ...f, qtyPerJob3bath: e.target.value }))} data-testid="input-qty-3bath" />
                </div>
              </div>
            </div>
            {supplyForm.costPerUnit && (
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Cost per cleaning job preview:</p>
                <div className="grid grid-cols-4 gap-2 text-center text-xs">
                  <div>
                    <p className="text-muted-foreground">Studio</p>
                    <p className="font-bold">${((parseFloat(supplyForm.costPerUnit) || 0) * (parseFloat(supplyForm.qtyPerJobStudio) || 0)).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">1BR</p>
                    <p className="font-bold">${((parseFloat(supplyForm.costPerUnit) || 0) * (parseFloat(supplyForm.qtyPerJob1br) || 0)).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">2BR</p>
                    <p className="font-bold">${((parseFloat(supplyForm.costPerUnit) || 0) * (parseFloat(supplyForm.qtyPerJob2br) || 0)).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">3BR</p>
                    <p className="font-bold">${((parseFloat(supplyForm.costPerUnit) || 0) * (parseFloat(supplyForm.qtyPerJob3br) || 0)).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1 block">Typical owner</label>
              <Select value={supplyForm.typicalOwner} onValueChange={v => setSupplyForm(f => ({ ...f, typicalOwner: v }))}>
                <SelectTrigger data-testid="input-supply-typical-owner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="org">Service Org (org-purchased)</SelectItem>
                  <SelectItem value="pm_client">PM Client (client-supplied)</SelectItem>
                  <SelectItem value="mixed">Mixed (varies per client)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Pre-fills "Purchased by" when restocking. Not enforced.</p>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Active</label>
              <Switch checked={supplyForm.active} onCheckedChange={v => setSupplyForm(f => ({ ...f, active: v }))} data-testid="toggle-supply-active" />
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90"
              disabled={!supplyForm.name || !supplyForm.costPerUnit || createSupply.isPending || updateSupply.isPending}
              onClick={handleSupplySubmit} data-testid="submit-supply-btn">
              {(createSupply.isPending || updateSupply.isPending) && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {editingSupply ? "Save Changes" : "Add Supply Item"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirmCost !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmCost(null); }}
        title="Delete this cost?"
        description={deleteConfirmCost ? <>
          This will permanently delete the <strong>${parseFloat(deleteConfirmCost.amount).toFixed(2)}</strong> cost{deleteConfirmCost.description ? <> ({deleteConfirmCost.description})</> : null}.
          {deleteConfirmCost.recurring && <span className="block mt-2 text-destructive font-medium">This is a recurring cost — deleting it stops future occurrences.</span>}
          <span className="block mt-2">This cannot be undone.</span>
        </> : null}
        confirmLabel="Delete Cost"
        pending={deleteCost.isPending}
        onConfirm={() => { if (deleteConfirmCost) { deleteCost.mutate(deleteConfirmCost.id); setDeleteConfirmCost(null); } }}
      />

      <DeleteConfirmDialog
        open={deleteConfirmSupply !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmSupply(null); }}
        title="Delete this supply item?"
        description={deleteConfirmSupply ? <>This will permanently delete the supply item <strong>"{deleteConfirmSupply.name}"</strong> from the catalog. Cleaning jobs that already deducted this supply remain unchanged. This cannot be undone.</> : null}
        confirmLabel="Delete Supply"
        pending={deleteSupply.isPending}
        onConfirm={() => { if (deleteConfirmSupply) { deleteSupply.mutate(deleteConfirmSupply.id); setDeleteConfirmSupply(null); } }}
      />
    </AdminLayout>
  );
}
